import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import makeStyles from '@mui/styles/makeStyles';
import React, { useState } from 'react';
import { errorToast, successToast } from '../../components/Toast';
import { AuthData } from '../../context/auth';
import { ClaimData, withClaimContext } from '../../context/claim';
import { PolicyData } from '../../context/policy';
import { ClaimUpdateRequest, SubClaimResponse } from '../../types/claim';
import { getCurrencySymbol } from '../../util/util';
import DefaultDialog from '../DefaultDialog';
import {
	checkInvalidReserveValue,
	formatRequestedReserve,
	formatReserve,
	getReserveError,
} from './util';

interface Props {
	authData: AuthData;
	claimData: ClaimData;
	subclaim: SubClaimResponse;
	policyData: PolicyData;
}

const useStyles = makeStyles((theme) => ({
	labelAsterisk: {
		color: 'red',
	},
}));

const UpdateReservesModal = (props: Props) => {
	const classes = useStyles();

	const { claimData, subclaim, policyData } = props;
	const { claim } = claimData;

	const [isOpen, setIsOpen] = useState(false);
	const [reserveType, setReserveType] = useState('loss');
	const [amount, setAmount] = useState('');
	const [reserveAmountError, setReserveAmountError] = useState('');
	const [loading, setLoading] = useState(false);
	const currentReserve =
		reserveType === 'loss'
			? formatReserve(subclaim.lossReserve.currentAmount, true)
			: formatReserve(subclaim.expenseReserve.currentAmount, true);

	React.useEffect(() => {
		// when switching reserve type -> reset amount and reserve error!
		setAmount('');
		setReserveAmountError('');
	}, [reserveType]);

	const close = () => {
		setIsOpen(false);
		// reset state
		setReserveType('loss');
		setAmount('');
	};
	const open = () => setIsOpen(true);

	const confirmUpdateReserve = async () => {
		setReserveAmountError('');
		setLoading(true);
		const reserveAmount = formatRequestedReserve(amount);
		const reserveUpdate =
			reserveType === 'loss'
				? { lossReserve: { amount: reserveAmount } }
				: { expenseReserve: { amount: reserveAmount } };

		if (checkInvalidReserveValue(amount)) {
			setReserveAmountError(getReserveError(amount));
			setLoading(false);
			return;
		}

		if (currentReserve === formatReserve(amount)) {
			setReserveAmountError(
				'New reserve amount must be different from the current amount',
			);
			setLoading(false);
			return;
		}

		const payload: ClaimUpdateRequest = {
			updateSubClaims: [
				{
					locator: subclaim.locator,
					...reserveUpdate,
				},
			],
		};
		try {
			await claimData.updateClaim(claim.locator, payload);
			successToast('Successfully updated reserve');
			close();
		} catch {
			errorToast('Failed to update reserve');
		}

		setLoading(false);
	};

	const heading = 'Update Reserve';

	const actions = [
		{
			text: 'Update Reserve',
			onClick: confirmUpdateReserve,
			isDisabled: loading,
		},
		{ text: 'Cancel', onClick: close },
	];

	const currencyVal = getCurrencySymbol(
		policyData && policyData.policy ? policyData.policy.currency : null,
	);

	const renderLossExpenseSelector = () => {
		return (
			<RadioGroup
				row
				name='lossExpenseSelector'
				value={reserveType}
				onChange={(e) => setReserveType(e.target.value)}
			>
				<FormControlLabel
					value='loss'
					control={<Radio color='primary' />}
					label='Loss'
					labelPlacement='end'
				/>
				<FormControlLabel
					value='expense'
					control={<Radio color='primary' />}
					label='Expense'
					labelPlacement='end'
				/>
			</RadioGroup>
		);
	};

	const renderAmountInput = () => {
		return (
			<div style={{ marginTop: '5px' }}>
				<TextField
					id='currentReserve'
					type='number'
					style={{ width: '40%', marginRight: '10px' }}
					label={'Current Available Reserve'}
					value={currentReserve}
					disabled
					InputProps={{
						startAdornment: (
							<InputAdornment position='start'>{currencyVal}</InputAdornment>
						),
					}}
				/>
				<TextField
					id='updateReserveInput'
					error={!!reserveAmountError}
					helperText={reserveAmountError}
					required
					type='number'
					style={{ width: '40%', marginRight: '10px' }}
					label={'New Available Reserve'}
					value={amount}
					disabled={false}
					onChange={(e) => setAmount(e.target.value)}
					onBlur={() => setAmount(formatReserve(amount))}
					InputProps={{
						startAdornment: (
							<InputAdornment position='start'>{currencyVal}</InputAdornment>
						),
					}}
					InputLabelProps={{
						classes: {
							asterisk: classes.labelAsterisk,
						},
					}}
				/>
			</div>
		);
	};

	const renderModalContent = () => {
		return (
			<div style={{ display: 'flex', flexDirection: 'column' }}>
				{renderLossExpenseSelector()}
				{renderAmountInput()}
			</div>
		);
	};

	return (
		<>
			<Button color='primary' variant='contained' onClick={open}>
				Update Reserve
			</Button>

			<DefaultDialog
				actions={actions}
				open={isOpen}
				onClose={close}
				title={heading}
			>
				{renderModalContent()}
			</DefaultDialog>
		</>
	);
};

export default withClaimContext<Props>(UpdateReservesModal);
